import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';

const HeroSection = styled.section`
  background: #232B3B;
  padding: 100px 20px 80px;
  margin-top: -80px;
  padding-top: 140px;
  color: white;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/650f1c8c6cf62f0b696c3d20_Lake2.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.15;
    z-index: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const PageTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  margin-bottom: 20px;
  letter-spacing: -1px;
`;

const PageSubtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 24px);
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.5;
`;

const SolutionsGrid = styled.div`
  padding: 80px 20px;
  background: #f8f9fa;
`;

const SolutionCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 60px;
  margin-bottom: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  
  &:nth-child(even) {
    grid-template-columns: 1fr 1fr;
    
    .solution-content {
      order: 2;
    }
    
    .solution-visual {
      order: 1;
    }
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    padding: 40px;
    gap: 30px;
    
    &:nth-child(even) {
      .solution-content {
        order: 1;
      }
      
      .solution-visual {
        order: 2;
      }
    }
  }
`;

const SolutionContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SolutionBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
  width: fit-content;
`;

const SolutionTitle = styled.h2`
  font-size: clamp(32px, 4vw, 42px);
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.2;
`;

const SolutionDescription = styled.p`
  font-size: 18px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  font-size: 16px;
  color: #555;
  
  &::before {
    content: '•';
    color: #4CAF50;
    font-weight: bold;
    margin-right: 12px;
    font-size: 20px;
    line-height: 1.2;
  }
`;

const SolutionCTA = styled(Link)`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 16px;
  display: inline-block;
  transition: all 0.3s;
  text-align: center;
  margin-right: 20px;
  margin-bottom: 10px;
  
  &:hover {
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(35, 43, 59, 0.3);
    background: linear-gradient(135deg, #1a1f2e 0%, #232B3B 100%);
  }
`;

const SecondaryCTA = styled(Link)`
  color: #232B3B;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s;
  
  &:hover {
    color: #ffd700;
    
    &::after {
      transform: translateX(5px);
    }
  }
  
  &::after {
    content: '→';
    margin-left: 8px;
    transition: transform 0.3s;
  }
`;

const SolutionVisual = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%);
    animation: pulse 3s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.3;
    }
  }
`;

const IconWrapper = styled.div`
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    border-radius: 20px;
    z-index: -1;
    opacity: 0.3;
  }
`;

const IconGraphic = styled.div<{ shape?: string }>`
  width: 100px;
  height: 100px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: white;
    clip-path: ${props => props.shape || 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'};
  }
`;

const CTASection = styled.section`
  background: white;
  padding: 100px 20px;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: clamp(32px, 4vw, 42px);
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
`;

const CTAText = styled.p`
  font-size: 20px;
  color: #666;
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButtonPrimary = styled(Link)`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
  padding: 18px 40px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 18px;
  display: inline-block;
  transition: all 0.3s;
  margin: 0 10px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  }
`;

const CTAButtonSecondary = styled(Link)`
  background: transparent;
  color: #232B3B;
  padding: 18px 40px;
  border: 2px solid #232B3B;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 18px;
  display: inline-block;
  transition: all 0.3s;
  margin: 0 10px;
  
  &:hover {
    background: #232B3B;
    color: white;
  }
`;

const QuestionSection = styled.section`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  padding: 100px 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    animation: pulse 4s ease-in-out infinite;
  }
`;

const QuestionContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const QuestionHeader = styled.h2`
  font-size: clamp(36px, 5vw, 48px);
  font-weight: 800;
  color: #232B3B;
  text-align: center;
  margin-bottom: 15px;
  letter-spacing: -1px;
`;

const QuestionSubheader = styled.p`
  font-size: clamp(18px, 2.5vw, 22px);
  color: #333;
  text-align: center;
  margin-bottom: 50px;
  font-weight: 600;
  line-height: 1.4;
`;

const QuestionBox = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  position: relative;
  margin-bottom: 40px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 30px 20px;
  }
`;

const QuestionText = styled.div`
  font-size: 18px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 30px;
  
  strong {
    color: #232B3B;
    font-weight: 700;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 16px;
  }
`;

const CopyButton = styled.button`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  color: white;
  padding: 18px 40px;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 auto;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(35, 43, 59, 0.3);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const Solutions: React.FC = () => {
  const handleCopyQuestion = () => {
    const question = `I am considering [or currently own] a [ANNUITY NAME] from [COMPANY NAME]. 

Based on actual historical performance data and real-world returns (not hypothetical illustrations), what are the key risks, limitations, and realistic return expectations for this specific annuity? Please include any concerns about managed volatility indices, date lottery effects, caps, participation rates, and how actual client returns have compared to illustrated returns. 

Also, how does this annuity's actual historical performance compare to simply investing in a basic S&P 500 index annuity over the same time periods?`;
    
    navigator.clipboard.writeText(question).then(() => {
      window.open('https://chat.openai.com/', '_blank');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  return (
    <SharedLayout>
      <HeroSection>
        <Container>
          <PageTitle>Innovative Solutions for Smart Retirement Planning</PageTitle>
          <PageSubtitle>
            Discover how our proprietary methods and AI-powered tools help you make 
            informed annuity decisions with confidence
          </PageSubtitle>
        </Container>
      </HeroSection>

      <QuestionSection>
        <QuestionContainer>
          <QuestionHeader>The Question That Changes Everything</QuestionHeader>
          <QuestionSubheader>
            6 advisors. 200+ years experience. 1 question that reveals the truth.
          </QuestionSubheader>
          
          <QuestionBox>
            <QuestionText>
              <strong>I am considering [or currently own] a [ANNUITY NAME] from [COMPANY NAME].</strong>
              <br /><br />
              Based on actual historical performance data and real-world returns (not hypothetical illustrations), 
              what are the key risks, limitations, and realistic return expectations for this specific annuity? 
              Please include any concerns about managed volatility indices, date lottery effects, caps, 
              participation rates, and how actual client returns have compared to illustrated returns.
              <br /><br />
              Also, how does this annuity's actual historical performance compare to simply investing in a 
              basic S&P 500 index annuity over the same time periods?
            </QuestionText>
            
            <CopyButton onClick={handleCopyQuestion}>
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
              </svg>
              Copy Question & Ask ChatGPT
            </CopyButton>
          </QuestionBox>
        </QuestionContainer>
      </QuestionSection>

      <SolutionsGrid>
        <Container>
          <SolutionCard>
            <SolutionContent className="solution-content">
              <SolutionBadge>Industry Problem</SolutionBadge>
              <SolutionTitle>The Date Lottery Problem</SolutionTitle>
              <SolutionDescription>
                Your annuity's performance shouldn't depend on luck. We've identified a critical 
                flaw in how annuities are sold - your start date can dramatically impact returns, 
                creating winners and losers by pure chance.
              </SolutionDescription>
              <FeatureList>
                <FeatureItem>Up to 300% difference in returns based on start date alone</FeatureItem>
                <FeatureItem>Affects millions of annuity holders nationwide</FeatureItem>
                <FeatureItem>Hidden from consumers by the industry</FeatureItem>
                <FeatureItem>Now exposed with our transparent analysis</FeatureItem>
              </FeatureList>
              <div>
                <SolutionCTA to="/averill-method#date-lottery">
                  Learn About Date Lottery
                </SolutionCTA>
                <SecondaryCTA to="/free-ebook">
                  Download Free Guide
                </SecondaryCTA>
              </div>
            </SolutionContent>
            <SolutionVisual className="solution-visual">
              <img 
                src="/roulette.jpg" 
                alt="The Date Lottery Problem - Annuity timing risk" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  borderRadius: '12px'
                }} 
              />
            </SolutionVisual>
          </SolutionCard>

          <SolutionCard>
            <SolutionContent className="solution-content">
              <SolutionBadge>AI-Powered Solution</SolutionBadge>
              <SolutionTitle>Deep Dive Analysis Tool</SolutionTitle>
              <SolutionDescription>
                Our proprietary deep dive tool that shows exactly how your annuity would have 
                performed using real historical data. No more guessing - see actual results based 
                on your specific contract terms.
              </SolutionDescription>
              <FeatureList>
                <FeatureItem>Test any annuity against 20+ years of market data</FeatureItem>
                <FeatureItem>Compare multiple products side-by-side</FeatureItem>
                <FeatureItem>See performance across different start dates</FeatureItem>
                <FeatureItem>100% free with instant results</FeatureItem>
              </FeatureList>
              <div>
                <SolutionCTA to="/ai-backtester">
                  Get Your Deep Dive Free
                </SolutionCTA>
                <SecondaryCTA to="/ai-backtester">
                  See How It Works
                </SecondaryCTA>
              </div>
            </SolutionContent>
            <SolutionVisual className="solution-visual">
              <img 
                src="/aibacktest.jpg" 
                alt="Deep Dive Tool - Historical performance analysis" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  borderRadius: '12px'
                }} 
              />
            </SolutionVisual>
          </SolutionCard>

          <SolutionCard>
            <SolutionContent className="solution-content">
              <SolutionBadge>Proven Methodology</SolutionBadge>
              <SolutionTitle>The Averill Method</SolutionTitle>
              <SolutionDescription>
                Our proprietary 26-point evaluation system, developed over 60 years and refined 
                across 10,000+ client cases. This comprehensive approach ensures you get an 
                annuity that truly fits your retirement goals.
              </SolutionDescription>
              <FeatureList>
                <FeatureItem>26 critical factors analyzed for every recommendation</FeatureItem>
                <FeatureItem>Personalized to your unique financial situation</FeatureItem>
                <FeatureItem>Time-tested with zero client complaints</FeatureItem>
                <FeatureItem>Full transparency - we show our own portfolios</FeatureItem>
              </FeatureList>
              <div>
                <SolutionCTA to="/averill-method">
                  Discover The Method
                </SolutionCTA>
                <SecondaryCTA to="/contact">
                  Get Personalized Analysis
                </SecondaryCTA>
              </div>
            </SolutionContent>
            <SolutionVisual className="solution-visual">
              <img 
                src="/averillmethod.jpg" 
                alt="The Averill Method - 26-point evaluation system" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  borderRadius: '12px'
                }} 
              />
            </SolutionVisual>
          </SolutionCard>
        </Container>
      </SolutionsGrid>

      <CTASection>
        <Container>
          <CTATitle>Ready to Make Smarter Annuity Decisions?</CTATitle>
          <CTAText>
            Join over 10,000 satisfied clients who've discovered the truth about their annuities
          </CTAText>
          <div>
            <CTAButtonPrimary to="/get-started">
              Get Started Free
            </CTAButtonPrimary>
            <CTAButtonSecondary to="/contact">
              Schedule Consultation
            </CTAButtonSecondary>
          </div>
        </Container>
      </CTASection>
    </SharedLayout>
  );
};